/**
 * Casos clínicos estructurados con disclosure progresivo
 * Cada caso tiene "ondas" de información que se revelan al ordenar exámenes
 */

import { ClinicalNodeData } from '../types/medical';
import { SNOMED_CT_SYSTEM, LOINC_SYSTEM } from '../types/fhir';

// ═══════════════════════════════════════════════════════════════════════════════
// ESTRUCTURA DE UN CASO PROGRESIVO
// ═══════════════════════════════════════════════════════════════════════════════

export interface OrderableExam {
  id: string;
  category: 'physical_exam' | 'laboratory' | 'imaging' | 'treatment';
  label: string;
  description: string;
  cost?: string;           // "Rápido" | "30 min" | "2 horas"
  turnaround: string;      // Tiempo de resultado
  nodes: ClinicalNodeData[];
  teachingPoint: string;   // Enseñanza al revelar
  criticalFind?: boolean;  // Hallazgo que cambia el diagnóstico
}

export interface CaseWave {
  id: string;
  label: string;
  description: string;
  autoReveal: boolean;     // Se muestra al iniciar el caso
  nodes: ClinicalNodeData[];
}

export interface ClinicalScenario {
  id: string;
  title: string;
  setting: string;          // "Urgencias, Hospital Central, 08:30 AM"
  presentation: string;     // Narración inicial del caso
  demographics: { age: number; sex: 'male' | 'female' | 'other'; weight?: number; height?: number };
  chiefComplaint: string;
  waves: CaseWave[];
  orderableExams: OrderableExam[];
  correctDiagnosis: string;
  correctTriage: string;
  discussionPoints: string[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// CASO 1: IAM COMPLETO
// ═══════════════════════════════════════════════════════════════════════════════

export const SCENARIO_IAM: ClinicalScenario = {
  id: 'iam',
  title: 'Dolor Torácico Agudo',
  setting: 'Servicio de Urgencias, Hospital Central. Lunes 08:30 AM.',
  presentation: 'Hombre de 67 años traído por SAMU. Refiere dolor torácico opresivo de 2 horas de evolución, inicio en reposo, irradiación a brazo izquierdo y mandíbula. Antecedentes: HTA, DM2, tabaquismo activo (40 paquetes/año).',
  demographics: { age: 67, sex: 'male', weight: 78, height: 172 },
  chiefComplaint: 'Dolor torácico opresivo de 2 horas',

  waves: [
    {
      id: 'wave-arrival',
      label: 'Llegada del paciente',
      description: 'El paciente ingresa consciente, pálido, diaforético.',
      autoReveal: true,
      nodes: [
        {
          id: 'complaint-1',
          type: 'complaint',
          concept: { code: '279035001', display: 'Dolor torácico opresivo', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          valueString: 'Opresivo, intenso (8/10), irradiación brazo izquierdo y mandíbula, 2h evolución',
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 6, volatility: 0.3, timeCriticality: 0.9 },
          visualization: { baseColor: '#DC2626', icon: '💔', pulse: true },
          relationships: [],
        },
        {
          id: 'hist-hta',
          type: 'history',
          concept: { code: '38341003', display: 'Hipertensión arterial', system: SNOMED_CT_SYSTEM },
          valueString: 'Diagnosticada hace 15 años, mal control',
          clinicalStatus: 'active',
          physics: { mass: 2, volatility: 0.2, timeCriticality: 0.3 },
          visualization: { baseColor: '#6B7280', icon: '📋' },
          relationships: [],
        },
        {
          id: 'hist-dm',
          type: 'history',
          concept: { code: '44054006', display: 'Diabetes Mellitus tipo 2', system: SNOMED_CT_SYSTEM },
          valueString: 'Diagnosticada hace 10 años, metformina',
          clinicalStatus: 'active',
          physics: { mass: 2, volatility: 0.2, timeCriticality: 0.3 },
          visualization: { baseColor: '#6B7280', icon: '📋' },
          relationships: [],
        },
        {
          id: 'hist-tabaco',
          type: 'history',
          concept: { code: '77176002', display: 'Tabaquismo activo', system: SNOMED_CT_SYSTEM },
          valueString: '40 paquetes/año',
          clinicalStatus: 'active',
          physics: { mass: 2, volatility: 0.2, timeCriticality: 0.2 },
          visualization: { baseColor: '#6B7280', icon: '🚬' },
          relationships: [],
        },
      ],
    },
    {
      id: 'wave-vitals',
      label: 'Signos vitales iniciales',
      description: 'La enfermera toma signos vitales al ingreso.',
      autoReveal: true,
      nodes: [
        {
          id: 'vital-fc',
          type: 'vital_sign',
          concept: { code: '8867-4', display: 'Frecuencia cardíaca', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 110, unit: 'lpm' },
          referenceRange: { low: 60, high: 100 },
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 0.8 },
          visualization: { baseColor: '#EF4444', icon: '💓', pulse: true },
          relationships: [{ targetId: 'complaint-1', type: 'associated-with', strength: 0.7, bidirectional: true }],
        },
        {
          id: 'vital-pa',
          type: 'vital_sign',
          concept: { code: '8480-6', display: 'Presión arterial sistólica', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 88, unit: 'mmHg' },
          referenceRange: { low: 90, high: 140 },
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 0.9 },
          visualization: { baseColor: '#EF4444', icon: '🩺' },
          relationships: [],
        },
        {
          id: 'vital-fr',
          type: 'vital_sign',
          concept: { code: '9279-1', display: 'Frecuencia respiratoria', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 22, unit: 'rpm' },
          referenceRange: { low: 12, high: 20 },
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 0.5 },
          visualization: { baseColor: '#3B82F6', icon: '🫁' },
          relationships: [],
        },
        {
          id: 'vital-spo2',
          type: 'vital_sign',
          concept: { code: '2708-6', display: 'SpO2', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 94, unit: '%' },
          referenceRange: { low: 95, high: 100 },
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 0.6 },
          visualization: { baseColor: '#3B82F6', icon: 'O₂' },
          relationships: [],
        },
        {
          id: 'vital-temp',
          type: 'vital_sign',
          concept: { code: '8310-5', display: 'Temperatura', system: LOINC_SYSTEM },
          valueQuantity: { value: 36.4, unit: '°C' },
          referenceRange: { low: 36.1, high: 37.2 },
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.05, timeCriticality: 0.1 },
          visualization: { baseColor: '#10B981', icon: '🌡️' },
          relationships: [],
        },
      ],
    },
  ],

  orderableExams: [
    {
      id: 'exam-cardio',
      category: 'physical_exam',
      label: 'Examen cardiovascular',
      description: 'Auscultación cardíaca, pulsos, ingurgitación yugular',
      turnaround: 'Inmediato',
      teachingPoint: 'R3 y R4 son hallazgos de disfunción ventricular izquierda. Los crepitantes basales sugieren congestión pulmonar incipiente (Killip II).',
      nodes: [
        {
          id: 'exam-aus',
          type: 'physical_exam',
          concept: { code: '37931006', display: 'Auscultación cardíaca', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          valueString: 'R1R2 presentes, R3 gallop, soplo sistólico II/VI mitral',
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 4, volatility: 0.2, timeCriticality: 0.6 },
          visualization: { baseColor: '#3B82F6', icon: '👋' },
          relationships: [{ targetId: 'complaint-1', type: 'associated-with', strength: 0.8, bidirectional: true }],
        },
        {
          id: 'exam-pulm',
          type: 'physical_exam',
          concept: { code: '48348007', display: 'Auscultación pulmonar', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'Crepitantes bibasales finos',
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 4, volatility: 0.2, timeCriticality: 0.5 },
          visualization: { baseColor: '#3B82F6', icon: '🫁' },
          relationships: [],
        },
      ],
    },
    {
      id: 'exam-ecg',
      category: 'imaging',
      label: 'Electrocardiograma (ECG)',
      description: 'ECG de 12 derivaciones',
      turnaround: '5 minutos',
      criticalFind: true,
      teachingPoint: 'La elevación del ST > 1mm en 2 derivaciones contiguas (V1-V4) con imagen especular en cara inferior confirma STEMI anterior. Esto activa el CÓDIGO INFARTO — tiempo puerta-balón < 90 minutos.',
      nodes: [
        {
          id: 'img-ecg',
          type: 'imaging',
          concept: { code: '11524-6', display: 'ECG 12 derivaciones', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueString: 'Ritmo sinusal, FC 110. Elevación ST V1-V4 (3-5mm). Imagen especular en DII, DIII, aVF. Sin ondas Q.',
          severity: 'life-threatening',
          clinicalStatus: 'active',
          physics: { mass: 8, volatility: 0.1, timeCriticality: 1.0 },
          visualization: { baseColor: '#06B6D4', icon: '📊', pulse: true, glowIntensity: 0.8 },
          relationships: [{ targetId: 'complaint-1', type: 'indicates', strength: 0.95, bidirectional: false }],
        },
      ],
    },
    {
      id: 'exam-troponin',
      category: 'laboratory',
      label: 'Troponina T ultrasensible',
      description: 'Biomarcador de daño miocárdico',
      turnaround: '20 minutos',
      criticalFind: true,
      teachingPoint: 'Troponina T 850 ng/L (normal < 14). Elevación > 60x el límite superior confirma necrosis miocárdica significativa. En contexto de dolor torácico + ST elevado = STEMI confirmado.',
      nodes: [
        {
          id: 'lab-trop',
          type: 'laboratory',
          concept: { code: '6598-7', display: 'Troponina T', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 850, unit: 'ng/L' },
          referenceRange: { high: 14 },
          severity: 'life-threatening',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0, timeCriticality: 1.0 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪', pulse: true, glowIntensity: 0.8 },
          relationships: [{ targetId: 'complaint-1', type: 'indicates', strength: 0.95, bidirectional: false }],
        },
      ],
    },
    {
      id: 'exam-hemogram',
      category: 'laboratory',
      label: 'Hemograma completo',
      description: 'Recuento celular y fórmula',
      turnaround: '15 minutos',
      teachingPoint: 'Leucocitosis leve es frecuente en IAM por respuesta de estrés. No sugiere infección en este contexto.',
      nodes: [
        {
          id: 'lab-wbc',
          type: 'laboratory',
          concept: { code: '6690-2', display: 'Leucocitos', system: LOINC_SYSTEM },
          valueQuantity: { value: 12400, unit: '/mm³' },
          referenceRange: { low: 4500, high: 11000 },
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0, timeCriticality: 0.3 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪' },
          relationships: [],
        },
        {
          id: 'lab-hb',
          type: 'laboratory',
          concept: { code: '718-7', display: 'Hemoglobina', system: LOINC_SYSTEM },
          valueQuantity: { value: 14.2, unit: 'g/dL' },
          referenceRange: { low: 13, high: 17 },
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0, timeCriticality: 0.1 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪' },
          relationships: [],
        },
      ],
    },
    {
      id: 'exam-coag',
      category: 'laboratory',
      label: 'Perfil de coagulación',
      description: 'TP, TTPK, INR',
      turnaround: '20 minutos',
      teachingPoint: 'Coagulación basal normal es importante antes de iniciar anticoagulación y para planificar el cateterismo.',
      nodes: [
        {
          id: 'lab-inr',
          type: 'laboratory',
          concept: { code: '6301-6', display: 'INR', system: LOINC_SYSTEM },
          valueQuantity: { value: 1.0, unit: '' },
          referenceRange: { low: 0.8, high: 1.2 },
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0, timeCriticality: 0.2 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪' },
          relationships: [],
        },
      ],
    },
    {
      id: 'exam-rx',
      category: 'imaging',
      label: 'Radiografía de tórax',
      description: 'AP portátil',
      turnaround: '10 minutos',
      teachingPoint: 'Cardiomegalia leve y redistribución vascular sugieren congestión temprana. Sin derrame ni neumotórax, lo que descarta estas causas de dolor torácico.',
      nodes: [
        {
          id: 'img-rx',
          type: 'imaging',
          concept: { code: '71020', display: 'Radiografía de Tórax AP', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueString: 'ICT 0.55 (leve cardiomegalia). Redistribución vascular apical. Sin derrame pleural. Sin neumotórax.',
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 8, volatility: 0.1, timeCriticality: 0.4 },
          visualization: { baseColor: '#06B6D4', icon: '📷' },
          relationships: [],
        },
      ],
    },
    {
      id: 'exam-gases',
      category: 'laboratory',
      label: 'Gases arteriales',
      description: 'pH, PaO2, PaCO2, HCO3, Lactato',
      turnaround: '15 minutos',
      teachingPoint: 'Lactato 2.8 mmol/L indica hipoperfusión tisular incipiente. pH y PaO2 confirman oxigenación aceptable pero no óptima.',
      nodes: [
        {
          id: 'lab-ph',
          type: 'laboratory',
          concept: { code: '2744-1', display: 'pH arterial', system: LOINC_SYSTEM },
          valueQuantity: { value: 7.35, unit: '' },
          referenceRange: { low: 7.35, high: 7.45 },
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0, timeCriticality: 0.5 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪' },
          relationships: [],
        },
        {
          id: 'lab-lactato',
          type: 'laboratory',
          concept: { code: '1968-7', display: 'Lactato', system: LOINC_SYSTEM },
          valueQuantity: { value: 2.8, unit: 'mmol/L' },
          referenceRange: { high: 2.0 },
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0, timeCriticality: 0.7 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪' },
          relationships: [],
        },
      ],
    },
    {
      id: 'tx-aspirin',
      category: 'treatment',
      label: 'Aspirina 300mg masticable',
      description: 'Antiagregante plaquetario de carga',
      turnaround: 'Inmediato',
      teachingPoint: 'Aspirina reduce mortalidad en IAM un 23% (ISIS-2). Debe administrarse ANTES del cateterismo. Masticable para absorción rápida.',
      nodes: [
        {
          id: 'tx-aas',
          type: 'treatment',
          concept: { code: '387458008', display: 'Aspirina 300mg VO', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          valueString: '300mg masticable, dosis única de carga',
          clinicalStatus: 'active',
          physics: { mass: 3, volatility: 0.1, timeCriticality: 0.8 },
          visualization: { baseColor: '#10B981', icon: '💊' },
          relationships: [{ targetId: 'complaint-1', type: 'treats', strength: 0.6, bidirectional: false }],
        },
      ],
    },
    {
      id: 'tx-heparin',
      category: 'treatment',
      label: 'Heparina no fraccionada',
      description: 'Anticoagulación para cateterismo',
      turnaround: 'Inmediato',
      teachingPoint: 'Heparina IV en bolo previo a cateterismo previene trombosis del introductor y stent. El ACT objetivo es 250-350 segundos.',
      nodes: [
        {
          id: 'tx-hep',
          type: 'treatment',
          concept: { code: '372877000', display: 'Heparina 5000 UI IV', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          valueString: '5000 UI bolo IV',
          clinicalStatus: 'active',
          physics: { mass: 3, volatility: 0.1, timeCriticality: 0.7, decayHalfLife: 3600 },
          visualization: { baseColor: '#10B981', icon: '💉' },
          relationships: [],
        },
      ],
    },
  ],

  correctDiagnosis: 'Infarto agudo de miocardio con elevación del segmento ST (STEMI) anterior',
  correctTriage: 'immediate',
  discussionPoints: [
    '¿Por qué el tiempo puerta-balón es crucial? Cada 30 minutos de retraso aumenta la mortalidad.',
    '¿Qué clasificación de Killip tiene este paciente? Killip II (crepitantes + R3).',
    '¿Cuándo NO dar aspirina? Alergia documentada, hemorragia activa.',
    '¿Qué alternativa si no hay hemodinamia disponible? Fibrinolisis dentro de 30 minutos.',
    '¿Cuál es el diagnóstico diferencial más peligroso de descartar? Disección aórtica — fibrinolisis sería letal.',
  ],
};

// ═══════════════════════════════════════════════════════════════════════════════
// CASO 2: SEPSIS
// ═══════════════════════════════════════════════════════════════════════════════

export const SCENARIO_SEPSIS: ClinicalScenario = {
  id: 'sepsis',
  title: 'Fiebre y alteración de conciencia',
  setting: 'Servicio de Urgencias, Hospital Regional. Jueves 22:15.',
  presentation: 'Mujer de 72 años traída por familiar. Refiere fiebre de 3 días y confusión progresiva desde hace 6 horas. Antecedente reciente de ITU tratada con ciprofloxacino, suspendido por "mejoría" hace 4 días.',
  demographics: { age: 72, sex: 'female', weight: 62, height: 158 },
  chiefComplaint: 'Fiebre + confusión de 6 horas',

  waves: [
    {
      id: 'wave-arrival',
      label: 'Llegada',
      description: 'Paciente obnubilada, piel caliente, diaforética.',
      autoReveal: true,
      nodes: [
        {
          id: 'sintoma-1',
          type: 'symptom',
          concept: { code: '386661006', display: 'Fiebre', system: SNOMED_CT_SYSTEM },
          system: 'infectious',
          valueString: '39.2°C axilar, inicio hace 3 días, refractaria a paracetamol',
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 5, volatility: 0.3, timeCriticality: 0.8 },
          visualization: { baseColor: '#EF4444', icon: '🌡️', pulse: true },
          relationships: [{ targetId: 'sintoma-2', type: 'associated-with', strength: 0.7, bidirectional: true }],
        },
        {
          id: 'sintoma-2',
          type: 'symptom',
          concept: { code: '40917007', display: 'Alteración del estado mental', system: SNOMED_CT_SYSTEM },
          system: 'neurological',
          valueString: 'GCS 13 (O4 V4 M5). Desorientada en tiempo y espacio.',
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 6, volatility: 0.2, timeCriticality: 0.9 },
          visualization: { baseColor: '#DC2626', icon: '🧠', pulse: true },
          relationships: [],
        },
        {
          id: 'hist-1',
          type: 'history',
          concept: { code: '68566005', display: 'Infección urinaria previa', system: SNOMED_CT_SYSTEM },
          valueString: 'ITU hace 7 días, ciprofloxacino suspendido prematuramente',
          clinicalStatus: 'active',
          physics: { mass: 2, volatility: 0.2, timeCriticality: 0.4 },
          visualization: { baseColor: '#6B7280', icon: '📋' },
          relationships: [],
        },
      ],
    },
    {
      id: 'wave-vitals',
      label: 'Signos vitales',
      description: 'Enfermería toma signos vitales al triage.',
      autoReveal: true,
      nodes: [
        {
          id: 'vital-1',
          type: 'vital_sign',
          concept: { code: '8480-6', display: 'Presión arterial sistólica', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 85, unit: 'mmHg' },
          referenceRange: { low: 90, high: 140 },
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 0.9 },
          visualization: { baseColor: '#EF4444', icon: '🩺', pulse: true },
          relationships: [],
        },
        {
          id: 'vital-2',
          type: 'vital_sign',
          concept: { code: '8867-4', display: 'Frecuencia cardíaca', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 122, unit: 'lpm' },
          referenceRange: { low: 60, high: 100 },
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 0.7 },
          visualization: { baseColor: '#EF4444', icon: '💓', pulse: true },
          relationships: [],
        },
        {
          id: 'vital-fr',
          type: 'vital_sign',
          concept: { code: '9279-1', display: 'Frecuencia respiratoria', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 26, unit: 'rpm' },
          referenceRange: { low: 12, high: 20 },
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 0.6 },
          visualization: { baseColor: '#3B82F6', icon: '🫁' },
          relationships: [],
        },
        {
          id: 'vital-temp',
          type: 'vital_sign',
          concept: { code: '8310-5', display: 'Temperatura', system: LOINC_SYSTEM },
          valueQuantity: { value: 39.2, unit: '°C' },
          referenceRange: { low: 36.1, high: 37.2 },
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 0.5 },
          visualization: { baseColor: '#EF4444', icon: '🌡️' },
          relationships: [],
        },
      ],
    },
  ],

  orderableExams: [
    {
      id: 'exam-lactate',
      category: 'laboratory',
      label: 'Lactato arterial',
      description: 'Marcador de hipoperfusión tisular',
      turnaround: '10 minutos',
      criticalFind: true,
      teachingPoint: 'Lactato > 2 mmol/L en contexto de infección define shock séptico. > 4 mmol/L: mortalidad > 40%. Objetivo: normalización en 6h (clearance).',
      nodes: [
        {
          id: 'lab-1',
          type: 'laboratory',
          concept: { code: '2524-7', display: 'Lactato', system: LOINC_SYSTEM },
          system: 'metabolic',
          valueQuantity: { value: 4.2, unit: 'mmol/L' },
          referenceRange: { high: 2.0 },
          severity: 'life-threatening',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0, timeCriticality: 1.0 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪', pulse: true, glowIntensity: 0.8 },
          relationships: [{ targetId: 'vital-1', type: 'indicates', strength: 0.9, bidirectional: false }],
        },
      ],
    },
    {
      id: 'exam-hemogram',
      category: 'laboratory',
      label: 'Hemograma + PCR',
      description: 'Leucocitos, fórmula, reactantes de fase aguda',
      turnaround: '20 minutos',
      teachingPoint: 'Leucocitosis > 12.000 con desviación izquierda + PCR elevada apoyan respuesta inflamatoria sistémica. No son específicos pero sí sensibles.',
      nodes: [
        {
          id: 'lab-2',
          type: 'laboratory',
          concept: { code: '6690-2', display: 'Leucocitos', system: LOINC_SYSTEM },
          valueQuantity: { value: 18400, unit: '/mm³' },
          referenceRange: { low: 4500, high: 11000 },
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0, timeCriticality: 0.5 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪' },
          relationships: [],
        },
        {
          id: 'lab-pcr',
          type: 'laboratory',
          concept: { code: '1988-5', display: 'Proteína C reactiva', system: LOINC_SYSTEM },
          valueQuantity: { value: 284, unit: 'mg/L' },
          referenceRange: { high: 5 },
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0, timeCriticality: 0.4 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪' },
          relationships: [],
        },
      ],
    },
    {
      id: 'exam-urine',
      category: 'laboratory',
      label: 'Sedimento y urocultivo',
      description: 'Análisis de orina y cultivo',
      turnaround: '30 minutos (sedimento) / 48h (cultivo)',
      teachingPoint: 'Piuria + nitritos + bacterias sugieren ITU como foco. Urocultivo antes de antibiótico no debe retrasar la primera dosis > 1 hora.',
      nodes: [
        {
          id: 'lab-urine',
          type: 'laboratory',
          concept: { code: '5778-6', display: 'Orina completa', system: LOINC_SYSTEM },
          system: 'renal',
          valueString: 'Leucocitos 500/campo, nitritos +, bacterias ++',
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 8, volatility: 0, timeCriticality: 0.6 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪' },
          relationships: [],
        },
      ],
    },
    {
      id: 'exam-cultures',
      category: 'laboratory',
      label: 'Hemocultivos x2',
      description: 'Dos sets de hemocultivos pre-antibiótico',
      turnaround: '24-72h',
      teachingPoint: 'Dos sets de hemocultivos (aerobios + anaerobios) ANTES del antibiótico aumentan el rendimiento y guían la terapia dirigida posterior.',
      nodes: [
        {
          id: 'lab-cultivo',
          type: 'laboratory',
          concept: { code: '600-7', display: 'Hemocultivo', system: LOINC_SYSTEM },
          valueString: 'Muestras tomadas, pendiente resultado 24-72h',
          clinicalStatus: 'active',
          physics: { mass: 4, volatility: 0.2, timeCriticality: 0.3 },
          visualization: { baseColor: '#6B7280', icon: '🧪' },
          relationships: [],
        },
      ],
    },
    {
      id: 'tx-fluids',
      category: 'treatment',
      label: 'Cristaloides 30 ml/kg IV',
      description: 'Resucitación con Ringer lactato en bolo',
      turnaround: 'Inmediato',
      teachingPoint: 'Surviving Sepsis 2021: 30 ml/kg de cristaloide balanceado en las primeras 3 horas. En esta paciente (62 kg): ~2000 ml de Ringer lactato.',
      nodes: [
        {
          id: 'tx-sf',
          type: 'treatment',
          concept: { code: '423967005', display: 'Ringer lactato IV', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          valueString: '2000 ml Ringer lactato en 30 min',
          clinicalStatus: 'active',
          physics: { mass: 3, volatility: 0.1, timeCriticality: 0.9 },
          visualization: { baseColor: '#10B981', icon: '💧' },
          relationships: [{ targetId: 'vital-1', type: 'treats', strength: 0.7, bidirectional: false }],
        },
      ],
    },
    {
      id: 'tx-antibiotics',
      category: 'treatment',
      label: 'Antibióticos empíricos IV',
      description: 'Ceftriaxona 2g + metronidazol 500mg IV',
      turnaround: 'Inmediato',
      teachingPoint: 'Antibiótico de amplio espectro dentro de 1h del reconocimiento de sepsis reduce mortalidad. Cobertura gram+/- y anaerobios según foco.',
      nodes: [
        {
          id: 'tx-atb',
          type: 'treatment',
          concept: { code: '386960009', display: 'Ceftriaxona 2g IV', system: SNOMED_CT_SYSTEM },
          system: 'infectious',
          valueString: 'Ceftriaxona 2g + metronidazol 500mg IV, dosis única de carga',
          clinicalStatus: 'active',
          physics: { mass: 3, volatility: 0.1, timeCriticality: 1.0 },
          visualization: { baseColor: '#10B981', icon: '💉' },
          relationships: [{ targetId: 'sintoma-1', type: 'treats', strength: 0.85, bidirectional: false }],
        },
      ],
    },
    {
      id: 'tx-vasopressors',
      category: 'treatment',
      label: 'Noradrenalina IV',
      description: 'Vasopresor para PAM ≥ 65 mmHg',
      turnaround: 'Inmediato',
      teachingPoint: 'Si la PAM no mejora tras los cristaloides, iniciar noradrenalina 0.05-0.5 µg/kg/min (vasopresor de primera línea en shock séptico).',
      nodes: [
        {
          id: 'tx-nora',
          type: 'treatment',
          concept: { code: '387480006', display: 'Noradrenalina IV', system: SNOMED_CT_SYSTEM },
          system: 'cardiovascular',
          valueString: '0.1 µg/kg/min titulando a PAM ≥ 65',
          clinicalStatus: 'active',
          physics: { mass: 3, volatility: 0.1, timeCriticality: 0.95 },
          visualization: { baseColor: '#10B981', icon: '💉' },
          relationships: [{ targetId: 'vital-1', type: 'treats', strength: 0.9, bidirectional: false }],
        },
      ],
    },
  ],

  correctDiagnosis: 'Shock séptico de foco urinario',
  correctTriage: 'immediate',
  discussionPoints: [
    '¿Qué define shock séptico vs sepsis? Necesidad de vasopresores + lactato > 2 mmol/L pese a fluidos adecuados.',
    '¿Por qué la antibioterapia dentro de 1h? Cada hora de retraso aumenta mortalidad ~7%.',
    '¿Cómo monitorizar la respuesta? Clearance de lactato, diuresis > 0.5 ml/kg/h, PAM ≥ 65 mmHg.',
    '¿Cuándo escalar a UCI? Desde el diagnóstico de shock séptico o disfunción orgánica múltiple.',
  ],
};

// ═══════════════════════════════════════════════════════════════════════════════
// CASO 3: EXACERBACIÓN EPOC
// ═══════════════════════════════════════════════════════════════════════════════

export const SCENARIO_EPOC: ClinicalScenario = {
  id: 'epoc',
  title: 'Disnea en paciente EPOC',
  setting: 'Servicio de Urgencias, Hospital Provincial. Domingo 06:40.',
  presentation: 'Hombre de 68 años, EPOC GOLD III conocido, ingresa por disnea progresiva y aumento de tos con expectoración purulenta en los últimos 3 días. Ex-fumador de 50 paquetes/año.',
  demographics: { age: 68, sex: 'male', weight: 71, height: 170 },
  chiefComplaint: 'Disnea progresiva + tos productiva de 3 días',

  waves: [
    {
      id: 'wave-arrival',
      label: 'Llegada',
      description: 'Paciente taquipneico, con tiraje intercostal, uso de musculatura accesoria.',
      autoReveal: true,
      nodes: [
        {
          id: 'sintoma-1',
          type: 'symptom',
          concept: { code: '267036007', display: 'Disnea', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'Progresiva 3 días, mMRC 4 (al reposo), uso de accesoria',
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 6, volatility: 0.3, timeCriticality: 0.9 },
          visualization: { baseColor: '#DC2626', icon: '🫁', pulse: true },
          relationships: [{ targetId: 'vital-2', type: 'associated-with', strength: 0.9, bidirectional: true }],
        },
        {
          id: 'sintoma-2',
          type: 'symptom',
          concept: { code: '49727002', display: 'Tos productiva', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'Aumento de esputo purulento, verdoso, 3 días',
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 4, volatility: 0.3, timeCriticality: 0.5 },
          visualization: { baseColor: '#F59E0B', icon: '💨' },
          relationships: [],
        },
        {
          id: 'hist-1',
          type: 'history',
          concept: { code: '13645005', display: 'EPOC GOLD III', system: SNOMED_CT_SYSTEM },
          valueString: 'EPOC severa dx hace 6 años, VEF1 basal 42%. 3 hospitalizaciones el último año.',
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 3, volatility: 0.1, timeCriticality: 0.6 },
          visualization: { baseColor: '#6B7280', icon: '📋' },
          relationships: [],
        },
      ],
    },
    {
      id: 'wave-vitals',
      label: 'Signos vitales',
      description: 'Enfermería toma signos vitales al ingreso.',
      autoReveal: true,
      nodes: [
        {
          id: 'vital-1',
          type: 'vital_sign',
          concept: { code: '9279-1', display: 'Frecuencia respiratoria', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 28, unit: 'rpm' },
          referenceRange: { low: 12, high: 20 },
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 0.9 },
          visualization: { baseColor: '#EF4444', icon: '🫁', pulse: true },
          relationships: [],
        },
        {
          id: 'vital-2',
          type: 'vital_sign',
          concept: { code: '2708-6', display: 'SpO2', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 86, unit: '%' },
          referenceRange: { low: 88, high: 92 },
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 1.0 },
          visualization: { baseColor: '#EF4444', icon: 'O₂', pulse: true, glowIntensity: 0.7 },
          relationships: [],
        },
        {
          id: 'vital-fc',
          type: 'vital_sign',
          concept: { code: '8867-4', display: 'Frecuencia cardíaca', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 112, unit: 'lpm' },
          referenceRange: { low: 60, high: 100 },
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 0.6 },
          visualization: { baseColor: '#EF4444', icon: '💓' },
          relationships: [],
        },
        {
          id: 'vital-pa',
          type: 'vital_sign',
          concept: { code: '8480-6', display: 'Presión arterial sistólica', system: LOINC_SYSTEM },
          system: 'cardiovascular',
          valueQuantity: { value: 148, unit: 'mmHg' },
          referenceRange: { low: 90, high: 140 },
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0.1, timeCriticality: 0.3 },
          visualization: { baseColor: '#F59E0B', icon: '🩺' },
          relationships: [],
        },
      ],
    },
  ],

  orderableExams: [
    {
      id: 'exam-aus',
      category: 'physical_exam',
      label: 'Auscultación pulmonar',
      description: 'Examen pulmonar completo',
      turnaround: 'Inmediato',
      teachingPoint: 'Sibilancias espiratorias difusas + hipoventilación generalizada son típicas de EPOC exacerbado. Crepitantes focales sugerirían neumonía asociada.',
      nodes: [
        {
          id: 'exam-pulm',
          type: 'physical_exam',
          concept: { code: '48348007', display: 'Auscultación pulmonar', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'Sibilancias espiratorias difusas, hipoventilación bilateral. Sin crepitantes focales.',
          severity: 'moderate',
          clinicalStatus: 'active',
          physics: { mass: 4, volatility: 0.2, timeCriticality: 0.6 },
          visualization: { baseColor: '#3B82F6', icon: '👋' },
          relationships: [],
        },
      ],
    },
    {
      id: 'exam-gases',
      category: 'laboratory',
      label: 'Gasometría arterial',
      description: 'pH, PaO2, PaCO2, HCO3, exceso de base',
      turnaround: '15 minutos',
      criticalFind: true,
      teachingPoint: 'pH 7.28 + PaCO2 68 = acidosis respiratoria aguda sobre crónica. Indica falla ventilatoria, necesita soporte (VMNI) urgente.',
      nodes: [
        {
          id: 'lab-1',
          type: 'laboratory',
          concept: { code: '2744-1', display: 'pH arterial', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 7.28, unit: '' },
          referenceRange: { low: 7.35, high: 7.45 },
          severity: 'life-threatening',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0, timeCriticality: 1.0 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪', pulse: true, glowIntensity: 0.8 },
          relationships: [],
        },
        {
          id: 'lab-2',
          type: 'laboratory',
          concept: { code: '2019-8', display: 'PaCO2', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueQuantity: { value: 68, unit: 'mmHg' },
          referenceRange: { low: 35, high: 45 },
          severity: 'severe',
          clinicalStatus: 'active',
          physics: { mass: 10, volatility: 0, timeCriticality: 0.9 },
          visualization: { baseColor: '#8B5CF6', icon: '🧪' },
          relationships: [],
        },
      ],
    },
    {
      id: 'exam-rx',
      category: 'imaging',
      label: 'Radiografía de tórax',
      description: 'Rx PA + lateral',
      turnaround: '20 minutos',
      teachingPoint: 'Hiperinsuflación sin condensación confirma exacerbación EPOC sin neumonía. Excluye neumotórax — complicación posible en EPOC severo.',
      nodes: [
        {
          id: 'img-rx',
          type: 'imaging',
          concept: { code: '36643-5', display: 'Radiografía tórax PA', system: LOINC_SYSTEM },
          system: 'respiratory',
          valueString: 'Hiperinsuflación bilateral, aplanamiento diafragmático. Sin condensaciones ni neumotórax.',
          clinicalStatus: 'active',
          physics: { mass: 6, volatility: 0.1, timeCriticality: 0.6 },
          visualization: { baseColor: '#06B6D4', icon: '📷' },
          relationships: [],
        },
      ],
    },
    {
      id: 'tx-oxygen',
      category: 'treatment',
      label: 'O2 Venturi 24%',
      description: 'Oxigenoterapia controlada',
      turnaround: 'Inmediato',
      teachingPoint: 'O2 controlado (FiO2 24-28%) con target SpO2 88-92%. FiO2 altas pueden causar narcosis por CO2 al abolir el drive hipóxico en EPOC.',
      nodes: [
        {
          id: 'tx-o2',
          type: 'treatment',
          concept: { code: '57485005', display: 'Oxigenoterapia controlada', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'Venturi FiO2 24%, target SpO2 88-92%',
          clinicalStatus: 'active',
          physics: { mass: 3, volatility: 0.1, timeCriticality: 1.0 },
          visualization: { baseColor: '#10B981', icon: '💨' },
          relationships: [{ targetId: 'vital-2', type: 'treats', strength: 0.85, bidirectional: false }],
        },
      ],
    },
    {
      id: 'tx-bronchodilators',
      category: 'treatment',
      label: 'Broncodilatadores nebulizados',
      description: 'Salbutamol 5mg + Ipratropio 0.5mg',
      turnaround: 'Inmediato',
      teachingPoint: 'SABA + antimuscarínico de acción corta son primera línea. Nebulización cada 20 min × 3 en la hora inicial si no hay respuesta.',
      nodes: [
        {
          id: 'tx-broncho',
          type: 'treatment',
          concept: { code: '372897005', display: 'Salbutamol + Ipratropio NBZ', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'Salbutamol 5mg + Ipratropio 0.5mg nebulizado',
          clinicalStatus: 'active',
          physics: { mass: 3, volatility: 0.1, timeCriticality: 0.8 },
          visualization: { baseColor: '#10B981', icon: '💊' },
          relationships: [{ targetId: 'sintoma-1', type: 'treats', strength: 0.8, bidirectional: false }],
        },
      ],
    },
    {
      id: 'tx-corticoids',
      category: 'treatment',
      label: 'Corticoides sistémicos',
      description: 'Hidrocortisona 200mg IV',
      turnaround: 'Inmediato',
      teachingPoint: 'Corticoides reducen duración de exacerbación y probabilidad de recaída. Hidrocortisona IV en fase aguda; prednisona 40mg VO × 5 días al alta.',
      nodes: [
        {
          id: 'tx-corti',
          type: 'treatment',
          concept: { code: '48933004', display: 'Hidrocortisona 200mg IV', system: SNOMED_CT_SYSTEM },
          valueString: 'Hidrocortisona 200mg IV dosis única de carga',
          clinicalStatus: 'active',
          physics: { mass: 3, volatility: 0.1, timeCriticality: 0.7 },
          visualization: { baseColor: '#10B981', icon: '💉' },
          relationships: [],
        },
      ],
    },
    {
      id: 'tx-noninvasive',
      category: 'treatment',
      label: 'Ventilación no invasiva (BiPAP)',
      description: 'IPAP 14 / EPAP 6, FiO2 ajustada',
      turnaround: 'Inmediato',
      teachingPoint: 'VMNI está indicada en acidosis respiratoria (pH < 7.35 + PaCO2 > 45). Reduce intubación y mortalidad. Contraindicada si inconsciencia o secreciones excesivas.',
      nodes: [
        {
          id: 'tx-vmni',
          type: 'treatment',
          concept: { code: '428311008', display: 'BiPAP no invasiva', system: SNOMED_CT_SYSTEM },
          system: 'respiratory',
          valueString: 'IPAP 14 cmH2O / EPAP 6 cmH2O, FiO2 30%',
          clinicalStatus: 'active',
          physics: { mass: 3, volatility: 0.1, timeCriticality: 0.95 },
          visualization: { baseColor: '#10B981', icon: '🫁' },
          relationships: [{ targetId: 'lab-1', type: 'treats', strength: 0.9, bidirectional: false }],
        },
      ],
    },
  ],

  correctDiagnosis: 'Exacerbación severa de EPOC con acidosis respiratoria',
  correctTriage: 'immediate',
  discussionPoints: [
    '¿Por qué FiO2 controlada y no 100%? En EPOC, la hiperoxia puede abolir el drive hipóxico y causar narcosis por CO2.',
    '¿Cuándo VMNI? pH < 7.35 + PaCO2 > 45 con fatiga respiratoria. Reduce intubación en 50%.',
    '¿Antibióticos? Si hay ≥2 criterios de Anthonisen: aumento disnea + esputo + purulencia.',
    '¿Cuándo intubar? Fracaso de VMNI, inestabilidad hemodinámica, GCS < 10.',
  ],
};

// ═══════════════════════════════════════════════════════════════════════════════
// REGISTRO DE ESCENARIOS
// ═══════════════════════════════════════════════════════════════════════════════

export const SCENARIOS: Record<string, ClinicalScenario> = {
  iam: SCENARIO_IAM,
  sepsis: SCENARIO_SEPSIS,
  epoc: SCENARIO_EPOC,
};
